/** Every date of a call is Italian wall-clock time (the rules' time zone). */
const ROME = 'Europe/Rome'

/**
 * An instant as the `date` and `time` values of the inputs, in Rome; with
 * `seconds` the time keeps them (`HH:mm:ss`), for an arrival a PEC certifies
 * to the second.
 */
export function romeInputs(iso: string, { seconds = false } = {}) {
	// `sv-SE` formats as `YYYY-MM-DD HH:mm(:ss)`, the shape the inputs expect.
	const [date = '', time = ''] = new Intl.DateTimeFormat('sv-SE', {
		timeZone: ROME,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		...(seconds ? { second: '2-digit' as const } : {}),
		hourCycle: 'h23'
	})
		.format(new Date(iso))
		.split(' ')
	return { date, time }
}

/** "lunedì 5 ottobre 2026, 09:00", in the reader's language, Rome time. */
export function formatRomeDateTime(iso: string, language: string) {
	return new Intl.DateTimeFormat(language, {
		timeZone: ROME,
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23'
	}).format(new Date(iso))
}

/** "5 ottobre 2026, 09:00", in the reader's language, Rome time: a moment in a record, without the weekday. */
export function formatRomeMoment(iso: string, language: string) {
	return new Intl.DateTimeFormat(language, {
		timeZone: ROME,
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23'
	}).format(new Date(iso))
}

/** "5 ottobre 2026": the Italian day of an instant. */
export function formatRomeDate(iso: string, language: string) {
	return new Intl.DateTimeFormat(language, {
		timeZone: ROME,
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	}).format(new Date(iso))
}

/**
 * "7 ottobre 2026" for a deadline at 24:00 Italian time: the instant is the
 * first moment of the next day, so the day shown is the one it ends.
 */
export function formatRomeLastDay(iso: string, language: string) {
	return formatRomeDate(new Date(Date.parse(iso) - 1).toISOString(), language)
}

/** "lunedì 5 ottobre 2026" for a calendar day `YYYY-MM-DD`. */
export function formatDay(day: string, language: string) {
	return new Intl.DateTimeFormat(language, {
		timeZone: 'UTC',
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	}).format(new Date(`${day}T00:00:00Z`))
}

/**
 * "5 ott, 09:00" in the reader's language, Rome time, with the year only when
 * it is not the current one: compact enough for a card.
 */
export function formatRomeCompact(iso: string, language: string) {
	const date = new Date(iso)
	const year = (value: Date) =>
		new Intl.DateTimeFormat('en', {
			timeZone: ROME,
			year: 'numeric'
		}).format(value)
	return new Intl.DateTimeFormat(language, {
		timeZone: ROME,
		day: 'numeric',
		month: 'short',
		...(year(date) === year(new Date()) ? {} : { year: 'numeric' }),
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23'
	}).format(date)
}

/** Calendar days from today to the day of `iso`, both counted in Rome. */
export function romeDaysFromToday(iso: string) {
	const day = (date: Date) =>
		new Intl.DateTimeFormat('sv-SE', { timeZone: ROME }).format(date)
	return Math.round(
		(Date.parse(day(new Date(iso))) - Date.parse(day(new Date()))) /
			86_400_000
	)
}

/** The instant of a `date` and a `time` (`HH:mm` or `HH:mm:ss`) read on an Italian clock, as an ISO string. */
export function romeInstant(date: string, time: string) {
	const full = time.length === 5 ? `${time}:00` : time
	const guess = Date.parse(`${date}T${full}Z`)
	const shown = romeInputs(new Date(guess).toISOString(), { seconds: true })
	const offset = Date.parse(`${shown.date}T${shown.time}Z`) - guess
	return new Date(guess - offset).toISOString()
}

/** A deadline split for "entro le 23:31 di mercoledì 7 ottobre": the clock and the day, in Italian time. */
export function formatRomeDeadline(iso: string, language: string) {
	const date = new Date(iso)
	return {
		time: new Intl.DateTimeFormat(language, {
			timeZone: ROME,
			hour: '2-digit',
			minute: '2-digit',
			hourCycle: 'h23'
		}).format(date),
		day: new Intl.DateTimeFormat(language, {
			timeZone: ROME,
			weekday: 'long',
			day: 'numeric',
			month: 'long'
		}).format(date)
	}
}
